import { useState } from 'react'
import { Link } from '@inertiajs/react'
import SeoHead from '../../components/SeoHead'
import MarketingFooter from '../../components/MarketingFooter'

const SIGNUP_HREF = '/users/sign_up?plan=fund&src=for-lenders-with-investors'

const PAIN_CARDS = [
  {
    title: 'Distributions stop being optional.',
    body:
      'Every payment that comes in has to be split, recorded, and reported.',
  },
  {
    title: 'Records become a fiduciary obligation.',
    body: '"Trust me" doesn\'t work when someone else\'s $100K is in the deal.',
  },
  {
    title: 'Tax forms multiply.',
    body: "Now you're producing K-1s, not just 1098s.",
  },
  {
    title: 'Sourcing changes.',
    body:
      'You need a way to show investors what\'s available without 12 emails per deal.',
  },
]

const FEATURES = [
  {
    title: 'The Deal Room.',
    body:
      'Post a deal. Invite investors via magic link. Collect soft commitments against a capacity bar. Lock the deal — commitments become participations automatically.',
  },
  {
    title: 'Automated distributions.',
    body:
      'Borrower pays. We split it across every investor on that loan, by their participation share, on the date the payment was effective. No more month-end spreadsheet rebuilds.',
  },
  {
    title: 'The investor portal.',
    body:
      'Your investors log in via magic link. They see their positions, distributions, and statements. You stop fielding "how much did I make last quarter?" emails.',
  },
]

const SECURITY_BULLETS = [
  {
    title: 'Encrypted at rest and in transit.',
    body:
      'All data stored in PostgreSQL with disk encryption. Tax IDs and sensitive identifiers use Active Record Encryption.',
  },
  {
    title: 'Hosted on SOC 2 Type II infrastructure.',
    body: 'Hosted on Render — SOC 2 Type II infrastructure provider, US-region only.',
  },
  {
    title: 'Stripe-only payment handling.',
    body:
      'We never see, store, or process your card. Same for any borrower or investor payment workflow — Stripe Checkout, redirect-based.',
  },
  {
    title: 'Investor portal access via magic link.',
    body: 'No passwords for investors to manage or leak. Tokens expire and rotate.',
  },
  {
    title: 'Owner / Member roles.',
    body:
      'When you delegate to an assistant, they get scoped access. They can record payments and manage loans. They cannot lock deal offerings, run distributions, or change billing.',
  },
  {
    title: 'Audit trail on every write.',
    body: 'Loans, payments, offerings — we record who created and last touched each record.',
  },
  {
    title: 'Privacy and DSAR.',
    body: 'US state privacy compliance covered. Submit a data request any time.',
  },
]

const FAQS = [
  {
    q: 'What counts as an investor?',
    a: "Anyone who puts capital into a loan alongside you in exchange for a share of the interest. Could be a friend, a family member, a business partner, or a silent LP. If they're getting pro-rata interest payments, they belong in LendSolo Fund.",
  },
  {
    q: 'Do I need to be an SEC-registered fund manager?',
    a: "No. LendSolo Fund is designed for solo lenders syndicating individual loans (Rule 506(b) friends-and-family style), not pooled-fund managers. You remain responsible for your own securities and state-lending compliance — LendSolo tracks and reports, it doesn't advise.",
  },
  {
    q: 'What if my investors are friends and family?',
    a: "That's exactly who this is built for. The records, distributions, and statements that matter in a 506(b) context are the same ones that matter to your brother-in-law writing the $50K check — LendSolo just makes them effortless to produce.",
  },
  {
    q: 'How do K-1s work?',
    a: "LendSolo exports per-investor K-1 data as a CSV your accountant can drop into their K-1 generation tool. We don't file the K-1s for you — we produce the underlying numbers (principal contributed, income allocated, distributions received) with an audit trail behind each line.",
  },
  {
    q: 'Can my assistant have access?',
    a: 'Yes. Invite a team member and assign them the Member role. They can record payments and manage loans. They cannot lock deal offerings, run distributions, or change billing — those stay Owner-only.',
  },
  {
    q: 'Are distributions actually automated, or do I review first?',
    a: 'You review first. Every payment split proposal lands in a batch you approve. We run the math — pro-rata by participation share, effective-date-aware — and wait for your confirmation before marking distributions as sent.',
  },
  {
    q: 'What happens to investor data if I cancel?',
    a: 'Every investor, loan, distribution, and statement stays exportable as CSV and PDF indefinitely. Cancel, export, and your records live on. We never hold your data hostage.',
  },
]

const SCHEMA = {
  '@graph': [
    {
      '@type': 'FinancialProduct',
      name: 'LendSolo Fund',
      description:
        'Loan syndication, investor participation tracking, automated distributions, and an investor portal — for solo private lenders syndicating individual loans.',
      url: 'https://lendsolo.com/for-lenders-with-investors',
      feesAndCommissionsSpecification: 'Fund tier: $99/month or $990/year',
      provider: {
        '@type': 'Organization',
        name: 'LendSolo',
        url: 'https://lendsolo.com',
      },
    },
    {
      '@type': 'Organization',
      name: 'LendSolo',
      url: 'https://lendsolo.com',
      description:
        'LendSolo builds loan management software for private lenders, including a Fund tier for those syndicating with investors.',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@lendsolo.com',
      },
    },
  ],
}

export default function ForLendersWithInvestors() {
  return (
    <>
      <SeoHead
        title="Software for Private Lenders With Investors — LendSolo Fund"
        description="Track investor participation, automate distributions, and run your deal room. Built for solo lenders syndicating individual loans. Fund tier $99/mo."
        canonicalPath="/for-lenders-with-investors"
        schema={SCHEMA}
      />

      <div className="min-h-screen" style={{ backgroundColor: '#f8f7f4' }}>
        <Header />
        <Hero />
        <PainSection />
        <FeatureTrio />
        <SecurityBlock />
        <FounderVoice />
        <PricingStrip />
        <FaqSection />
        <FinalCta />
        <MarketingFooter />
      </div>
    </>
  )
}

function Header() {
  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-[#e2e0d8] sticky top-0 z-40">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#0f1a2e' }}
          >
            <span className="text-[#34d399] font-bold text-sm font-mono">LS</span>
          </div>
          <span className="text-lg font-bold text-[#0f1a2e] font-display">LendSolo</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            href="/"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Product
          </Link>
          <Link
            href="/deal-room"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Deal Room
          </Link>
          <Link
            href="/users/sign_in"
            className="text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Sign In
          </Link>
          <Link
            href={SIGNUP_HREF}
            className="text-sm font-semibold text-white bg-[#0f1a2e] hover:-translate-y-0.5 hover:shadow-lg transition-all px-4 py-2 rounded-lg"
          >
            Start Fund Trial
          </Link>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-20 sm:pt-28 pb-20 sm:pb-24">
      <div
        className="absolute top-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(26,122,76,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-5">
          <span className="w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
          Fund tier — for lenders with investors
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] text-[#0f1a2e] max-w-[820px] mx-auto tracking-tight">
          When you're <em className="italic text-[#1a7a4c]">not lending alone</em>.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#5a6578] max-w-[680px] mx-auto">
          The moment you bring on your first investor, your spreadsheet becomes a liability. LendSolo Fund handles fractional participation, automated distributions, investor statements, and a deal room — without the institutional price tag.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link
            href={SIGNUP_HREF}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Start a 60-day Fund trial
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10m0 0L9 4m4 4L9 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          <Link
            href="/deal-room"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-[#0f1a2e] rounded-[10px] text-[15px] font-semibold border-[1.5px] border-[#e2e0d8] hover:border-[#5a6578] hover:bg-white transition-all"
          >
            See the deal room
          </Link>
        </div>
      </div>
    </section>
  )
}

function PainSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          The stakes change
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[820px] mb-12">
          What changes when investor money is involved.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PAIN_CARDS.map((card) => (
            <div
              key={card.title}
              className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-[14px] p-7"
            >
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {card.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{card.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FeatureTrio() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Fund-tier features
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[780px] mb-12">
          Three things that make syndication actually tractable.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="bg-white border border-[#e2e0d8] rounded-[14px] p-7 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
            >
              <div className="font-mono text-xs text-[#1a7a4c] mb-3">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {f.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function SecurityBlock() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1000px] mx-auto px-6 sm:px-8">
        <div
          className="rounded-[16px] border-l-[4px] border border-[#e2e0d8] p-8 sm:p-10 shadow-[0_2px_16px_rgba(26,122,76,0.04)]"
          style={{ borderLeftColor: '#1a7a4c', backgroundColor: '#f8f7f4' }}
        >
          <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
            Security & data handling
          </p>
          <h2 className="font-display text-3xl sm:text-[36px] leading-[1.2] text-[#0f1a2e] tracking-tight mb-4">
            How LendSolo handles your data.
          </h2>
          <p className="text-[17px] leading-relaxed text-[#5a6578] mb-8 max-w-[720px]">
            When investor money is involved, every "is your data safe?" question gets asked twice. Here's what we do.
          </p>

          <ul className="space-y-5 mb-8">
            {SECURITY_BULLETS.map((b) => (
              <li key={b.title} className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-[#1a7a4c] mt-0.5 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M16.25 5L7.5 13.75 3.75 10"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <span className="font-semibold text-[#0f1a2e]">{b.title} </span>
                  <span className="text-[15px] leading-relaxed text-[#5a6578]">
                    {b.body}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          <div
            className="rounded-[12px] border border-[#e2e0d8] p-5 sm:p-6"
            style={{ backgroundColor: '#fef9ef' }}
          >
            <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#8a6b1a] font-medium mb-2">
              An honest disclaimer
            </p>
            <p className="text-[14px] leading-relaxed text-[#5a5030]">
              LendSolo is a tracking and reporting platform — not a regulated custodian, broker-dealer, or transfer agent. We don't move money. You handle ACH and wires through your bank. We handle the records. Your legal and securities compliance remains yours.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function FounderVoice() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[820px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Founder
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-6">
          Built by a private lender, not a software company.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] mb-4">
          When I started taking outside capital into my deals, every weekend became a distribution spreadsheet. Fund tier exists because I needed it first — institutional-grade records without institutional software pricing.
        </p>
        <p className="text-sm font-semibold text-[#0f1a2e]">— Pete, founder</p>
      </div>
    </section>
  )
}

function PricingStrip() {
  return (
    <section className="py-16 sm:py-20 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <div
          className="border border-[#e2e0d8] rounded-[14px] p-6 sm:p-8"
          style={{ backgroundColor: '#f8f7f4' }}
        >
          <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-2">
            Fund tier pricing
          </p>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="font-display text-2xl sm:text-3xl text-[#0f1a2e]">
                $99<span className="text-base text-[#5a6578]">/month</span>
                <span className="text-[#8c95a6] mx-3">·</span>
                $990<span className="text-base text-[#5a6578]">/year</span>
              </p>
              <p className="mt-2 text-[15px] text-[#5a6578] max-w-[620px]">
                Compared to SyndicationPro $400–600/mo and Juniper Square $1,200+/mo. Unlimited loans, unlimited investors, deal room included.
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Link
                href="/#pricing"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0f1a2e] border-[1.5px] border-[#e2e0d8] rounded-lg hover:border-[#5a6578] transition-all"
              >
                Compare tiers
              </Link>
              <Link
                href={SIGNUP_HREF}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0f1a2e] rounded-lg hover:-translate-y-0.5 hover:shadow-lg transition-all"
              >
                Start 60-day trial
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[720px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          FAQ
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-10">
          Common questions
        </h2>
        <div className="divide-y divide-[#e2e0d8]">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i
            return (
              <div key={item.q} className="py-6">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="text-base font-semibold text-[#0f1a2e] pr-4">
                    {item.q}
                  </span>
                  <span className="text-xl font-light text-[#8c95a6] min-w-[24px] text-center">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[500px] pt-4 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-[15px] leading-relaxed text-[#5a6578]">{item.a}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Ready?
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[720px] mx-auto mb-4">
          60-day free trial. No credit card for the first 14 days.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mx-auto mb-9">
          Import your portfolio, invite your investors, post your first deal.
        </p>
        <Link
          href={SIGNUP_HREF}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          Start a 60-day Fund trial
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8h10m0 0L9 4m4 4L9 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </section>
  )
}
